import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import type { Client, PaginationMeta, ClientFilter } from '@/types/admin';
import { formatRelativeTime } from '@/lib/date-utils';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

interface ClientsTableProps {
  clients: Client[];
  meta: PaginationMeta;
  onClientClick: (id: string) => void;
  onFilterChange: (filter: ClientFilter) => void;
  onSearchChange: (search: string) => void;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

const FILTER_LABELS: Record<ClientFilter, string> = {
  all: 'Todos',
};

export function ClientsTable({
  clients,
  meta,
  onClientClick,
  onFilterChange,
  onSearchChange,
  onPageChange,
  loading = false,
}: ClientsTableProps) {
  const [searchInput, setSearchInput] = useState('');
  const [currentFilter, setCurrentFilter] = useState<ClientFilter>('all');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange(searchInput);
  };

  const handleFilterChange = (value: string) => {
    const filter = value as ClientFilter;
    setCurrentFilter(filter);
    onFilterChange(filter);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Select value={currentFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-full min-h-11 lg:w-48">
            <SelectValue placeholder="Filtrar por..." />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(FILTER_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <div className="relative flex-1 lg:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              placeholder="Buscar por email o nombre..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="min-h-11 pl-9"
            />
          </div>
          <Button type="submit" disabled={loading} className="min-h-11">
            Buscar
          </Button>
        </form>
      </div>

      <div className="md:hidden space-y-3">
        {loading && <p className="py-8 text-center text-gray-500">Cargando...</p>}
        {!loading && clients.length === 0 && (
          <p className="py-8 text-center text-gray-500">No se encontraron clientes</p>
        )}
        {clients.map((client) => (
          <button
            key={client.id}
            type="button"
            onClick={() => onClientClick(client.id)}
            className="w-full rounded-lg border bg-white p-4 text-left"
          >
            <p className="font-medium text-gray-900 break-all">{client.email}</p>
            <p className="mt-1 text-sm text-gray-600">{client.name || 'Sin nombre'}</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">{formatRelativeTime(client.last_visit)}</span>
              <Badge variant="secondary">{client.memories_count} memorias</Badge>
            </div>
          </button>
        ))}
      </div>

      <div className="hidden rounded-lg border bg-white md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead className="hidden lg:table-cell">Nombre</TableHead>
              <TableHead>Última visita</TableHead>
              <TableHead className="text-center"># Memorias</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                  No se encontraron clientes
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.id} className="cursor-pointer" onClick={() => onClientClick(client.id)}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium text-gray-900">{client.email}</div>
                      <div className="lg:hidden text-sm text-gray-500">{client.name || 'Sin nombre'}</div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {client.name || <span className="text-gray-400 italic">Sin nombre</span>}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">{formatRelativeTime(client.last_visit)}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">{client.memories_count}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {meta.total_pages > 1 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-500">
            Mostrando {((meta.page - 1) * meta.per_page) + 1} a{' '}
            {Math.min(meta.page * meta.per_page, meta.total)} de {meta.total} clientes
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(meta.page - 1)}
              disabled={meta.page === 1 || loading}
              className="min-h-11"
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <span className="text-sm text-gray-700">
              Página {meta.page} de {meta.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(meta.page + 1)}
              disabled={meta.page === meta.total_pages || loading}
              className="min-h-11"
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
